import { MASTERY_THRESHOLD_MASTERED } from "./constants.js";
import { calculateMasteryLevel } from "./utils.js";

/**
 * Calculates forecasting metrics based on user history.
 * @param {Object} history - The user's question history map.
 * @param {Array} allQuestions - All available questions.
 * @returns {Object} Forecast metrics.
 */
export const calculateForecast = (history, allQuestions) => {
  const totalQuestions = allQuestions.length;
  const validIds = new Set(allQuestions.map(q => q.id));

  let totalAnswered = 0;
  let seenCount = 0;
  let masteredCount = 0;

  // 1. Current Snapshot
  Object.entries(history).forEach(([id, attempts]) => {
    if (!validIds.has(id)) return;

    totalAnswered += attempts.length;
    if (attempts.length > 0) seenCount++;

    const mastery = calculateMasteryLevel(attempts);
    if (mastery >= MASTERY_THRESHOLD_MASTERED) masteredCount++;
  });

  // 2. Velocity Calculation (Last 5 Days)
  const today = new Date();

  const dailyNew = {}; // { dateStr: count }
  const dailyMastered = {}; // { dateStr: count }

  // Initialize last 5 days with 0
  const daysToCheck = 5;
  for (let i = 0; i < daysToCheck; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split("T")[0];
    dailyNew[dateStr] = 0;
    dailyMastered[dateStr] = 0;
  }

  // Analyze history to find WHEN things happened
  Object.entries(history).forEach(([id, attempts]) => {
    if (!validIds.has(id)) return;
    if (attempts.length === 0) return;

    // When was it first seen?
    const firstDate = new Date(attempts[0].date);
    const firstDateStr = firstDate.toISOString().split("T")[0];
    if (dailyNew.hasOwnProperty(firstDateStr)) {
      dailyNew[firstDateStr]++;
    }

    // When was it mastered?
    // Mastery is >= 4 (3 consecutive correct).
    // Find the attempt index where it BECAME mastered.
    let streak = 0;
    for (let i = 0; i < attempts.length; i++) {
      if (attempts[i].correct) {
        streak++;
      } else {
        streak = 0;
      }

      // If we hit mastery threshold
      if (streak === 3) { // 3 consecutive correct = Level 4 (Mastered)
        const masterDate = new Date(attempts[i].date);
        const masterDateStr = masterDate.toISOString().split("T")[0];
        // Only count the FIRST time it became mastered (simplification)
        // Ideally we track if it dipped and came back, but for velocity, first mastery is good enough signal.
        if (dailyMastered.hasOwnProperty(masterDateStr)) {
          dailyMastered[masterDateStr]++;
        }
        break; // Stop checking this question
      }
    }
  });

  // Calculate Averages
  const sumNew = Object.values(dailyNew).reduce((a, b) => a + b, 0);
  const sumMastered = Object.values(dailyMastered).reduce((a, b) => a + b, 0);

  const applyHybridRounding = (v) => {
    if (v >= 1) return Math.round(v);
    if (v > 0) return Math.round(v * 10) / 10;
    return 0;
  };

  // Use daysToCheck, but if history is shorter, maybe use fewer?
  // For robustness, dividing by 5 is safe even if user started yesterday (velocity will just be low, which is technically true).
  const velocitySeen = applyHybridRounding(sumNew / daysToCheck);
  const velocityMastered = applyHybridRounding(sumMastered / daysToCheck);

  // 3. Projections
  const remainingSeen = totalQuestions - seenCount;
  const daysToSeeAll = velocitySeen > 0 ? Math.ceil(remainingSeen / velocitySeen) : Infinity;

  const remainingMastered = totalQuestions - masteredCount;
  const daysToMasterAll = velocityMastered > 0 ? Math.ceil(remainingMastered / velocityMastered) : Infinity;

  const addDays = (days) => {
    if (days === Infinity) return null;
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().split("T")[0];
  };

  return {
    totalQuestions,
    totalAnswered,
    seenCount,
    masteredCount,
    velocitySeen,
    velocityMastered,
    daysToSeeAll,
    daysToMasterAll,
    dateToSeeAll: addDays(daysToSeeAll),
    dateToMasterAll: addDays(daysToMasterAll),
  };
};

/**
 * Calculates encouraging insights based on history.
 * @param {Object} history - User history
 * @param {Array} allQuestions - Question DB
 * @returns {Object} Insights { currentStreak, totalExamsPassed, totalCorrect }
 */
export const calculateInsights = (history, allQuestions) => {
  const validIds = new Set(allQuestions.map((q) => q.id));
  const dailyStats = {};
  let totalCorrect = 0;
  let totalAnswered = 0;

  Object.entries(history).forEach(([id, attempts]) => {
    if (!validIds.has(id)) return;
    attempts.forEach((a) => {
      totalAnswered++;
      if (a.correct) totalCorrect++;

      const dateStr = new Date(a.date).toISOString().split("T")[0];
      if (!dailyStats[dateStr]) dailyStats[dateStr] = { correct: 0, total: 0 };
      dailyStats[dateStr].total++;
      if (a.correct) dailyStats[dateStr].correct++;
    });
  });

  // Calculate Streak (Consecutive days with >= 60% success)
  const sortedDates = Object.keys(dailyStats).sort((a, b) => b.localeCompare(a)); // Descending
  let currentStreak = 0;

  // Check if today/yesterday are present to start the streak
  // If the user hasn't practiced today yet, look at yesterday.
  const today = new Date().toISOString().split("T")[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];

  let startIndex = 0;
  // If latest data is not today or yesterday, streak is broken/0.
  if (sortedDates.length > 0) {
    if (sortedDates[0] !== today && sortedDates[0] !== yesterday) {
      currentStreak = 0;
    } else {
      for (const date of sortedDates) {
        const day = dailyStats[date];
        const rate = day.correct / day.total;
        if (rate >= 0.6) {
          currentStreak++;
        } else {
          break; // Streak broken
        }
      }
    }
  }

  // Equivalent Exams (50 questions per exam, 60% pass rate? No, just raw volume of correct answers)
  // User phrased it: "you've done 200 questions with 85% success rate, that means you passed 4 exams in a row!"
  // Let's count "Total Correct / 50" as "Exams worth of correct answers".
  const totalExamsPassed = Math.floor(totalCorrect / 50);

  // Weekly Improvement
  const now = new Date();
  const week1 = []; // last 7 days
  const week2 = []; // 7-14 days ago
  Object.entries(dailyStats).forEach(([dateStr, stats]) => {
    const date = new Date(dateStr);
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays < 7) week1.push(stats);
    else if (diffDays < 14) week2.push(stats);
  });

  const calcRate = (week) => {
    const total = week.reduce((sum, s) => sum + s.total, 0);
    const correct = week.reduce((sum, s) => sum + s.correct, 0);
    return total > 0 ? correct / total : null;
  };

  const rate1 = calcRate(week1);
  const rate2 = calcRate(week2);
  const improvement = rate1 !== null && rate2 !== null ? Math.round((rate1 - rate2) * 100) : null;

  return {
    currentStreak,
    totalExamsPassed,
    totalAnswered,
    totalCorrect,
    improvement,
  };
};
